import classNames from "classnames";
import React from "react";

type BannerProps = {
  children?: React.ReactNode;
  size?: "sm";
  title?: string;
  variant?: "warning" | "info" | "danger";
  image?: React.ReactNode;
  actionButtons?: React.ReactNode;
  customLayout?: boolean;
  onDismiss?: (e: React.MouseEvent) => void;
};

function Banner({
  children,
  size,
  title,
  variant = "warning",
  image,
  actionButtons,
  customLayout = false,
  onDismiss,
}: BannerProps) {
  const bgColor =
    variant === "warning"
      ? "bg-chart-yellow-faint"
      : variant === "danger"
        ? "bg-chart-red-faint"
        : "bg-bg-faint/50";
  const icon = variant === "danger" ? "icon-triangle-alert" : "icon-info";

  // If using custom layout with image and/or action buttons
  if (customLayout) {
    return (
      <div
        data-testid="banner"
        className={classNames(
          "w-full relative",
          size == "sm" ? "p-3 rounded-xl" : "px-4 py-3 rounded-2xl",
          bgColor
        )}
      >
        {/* Desktop layout */}
        <div className="hidden md:flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {image && <div className="shrink-0">{image}</div>}
            <div className="flex-1">
              <h2
                className={classNames(
                  "flex items-center text-fg-base",
                  size == "sm" ? "tiny-md" : "sm-md"
                )}
              >
                {title}
              </h2>
              {!!children && (
                <div
                  className={classNames(
                    "mt-1 text-fg-base",
                    size == "sm" ? "tiny-normal" : "sm-normal"
                  )}
                >
                  {children}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {actionButtons}
            {onDismiss && (
              <button className="p-1" onClick={onDismiss} aria-label="Close">
                <i className="icon-x text-fg-base text-lg" />
              </button>
            )}
          </div>
        </div>

        {/* Mobile layout */}
        <div className="md:hidden">
          {/* Dismiss button positioned absolutely in top right */}
          {onDismiss && (
            <button
              className="absolute top-2 right-2 p-1"
              onClick={onDismiss}
              aria-label="Close"
            >
              <i className="icon-x text-fg-base text-lg" />
            </button>
          )}

          <div className="flex flex-col gap-3">
            <div className={classNames(onDismiss ? "pr-8" : "")}>
              {" "}
              {/* Add padding only if dismiss button exists */}
              <h2
                className={classNames(
                  "flex items-center text-fg-base",
                  size == "sm" ? "tiny-md" : "sm-md"
                )}
              >
                {title}
              </h2>
              {!!children && (
                <div
                  className={classNames(
                    "mt-1 text-fg-base",
                    size == "sm" ? "tiny-normal" : "sm-normal"
                  )}
                >
                  {children}
                </div>
              )}
            </div>
            {/* Action buttons for mobile - full width */}
            {actionButtons && <div className="w-full">{actionButtons}</div>}
          </div>
        </div>
      </div>
    );
  }

  // Original Banner layout
  return (
    <div
      data-testid="banner"
      className={classNames(
        "w-full",
        size == "sm" ? "p-3 rounded-xl" : "px-4 py-3 rounded-2xl",
        bgColor
      )}
    >
      <h2 className="flex items-center">
        <i className={classNames(size == "sm" ? "text-lg" : "text-xl", icon)} />
        <span
          className={classNames(
            "ml-2 text-fg-base",
            size == "sm" ? "tiny-md" : "sm-md"
          )}
        >
          {title}
        </span>
      </h2>
      {!!children && (
        <div
          className={classNames(
            "mt-2 text-fg-base",
            size == "sm" ? "tiny-normal" : "sm-normal"
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export default Banner;
